import React, { useMemo, useState } from 'react'

import { NodeFeature, PublicNode, User } from '../../../__generated__/api'
import { useTerm } from '../../../common/hooks/useNode'
import { getSsnAge } from '../../../common/utils/ssn'
import { AGE_CHECK_THRESHOLD } from '../../../constants'
import GenericModalWrapper from '../components/GenericModalWrapper'

interface Props {
  onBackToStart: () => void
  setShouldDisplayPaymentTypeSelect: (shouldDisplay: boolean) => void
  selectedUser: User
  selectedNode: PublicNode | null
  selectedInsuranceContractId: number | null
  isOHCSide: boolean
  selectedNodeConnectionId?: string
  selectedServiceId?: number
}

export const useFreedomOfChoice = ({
  selectedUser,
  selectedNode,
  selectedInsuranceContractId,
  isOHCSide,
  selectedNodeConnectionId,
  selectedServiceId,
  onBackToStart,
  setShouldDisplayPaymentTypeSelect,
}: Props) => {
  // User has acknowledged that they want to continue with the flow (depends on the modal type where it leads to)
  const [freedomOfChoiceContinueAcknowledged, setFreedomOfChoiceContinueAcknowledged] =
    useState<boolean>(false)

  const selectedTerm = useTerm(
    selectedNodeConnectionId,
    selectedServiceId ? `${selectedServiceId}` : undefined,
    ['service_package', 'service']
  )

  // Main flow: Check if the selected service is a freedom of choice service
  const shouldDisplayFreedomOfChoiceAllowedModal = useMemo(() => {
    const age = getSsnAge(selectedUser?.ssn)
    const isFreedomOfChoiceEnabled = selectedTerm?.freedomOfChoice
    return isFreedomOfChoiceEnabled && !freedomOfChoiceContinueAcknowledged && age
      ? age >= AGE_CHECK_THRESHOLD
      : false
  }, [freedomOfChoiceContinueAcknowledged, selectedTerm?.freedomOfChoice, selectedUser?.ssn])

  const shouldDisplayFreedomOfChoiceDisallowedModal = useMemo(() => {
    const age = getSsnAge(selectedUser?.ssn)
    const isFreedomOfChoiceEnabled = selectedTerm?.freedomOfChoice
    return isFreedomOfChoiceEnabled && age ? age < AGE_CHECK_THRESHOLD : false
  }, [selectedTerm?.freedomOfChoice, selectedUser?.ssn])

  // Redirect flow: Check if the selected node is a freedom of choice redirect node
  const shouldDisplayFreedomOfChoiceRedirectModal = useMemo(() => {
    const age = getSsnAge(selectedUser?.ssn)
    const isAgeCheckEnabled = selectedNode?.features?.includes(NodeFeature.AgeCheckEnabled)
    return isAgeCheckEnabled && !freedomOfChoiceContinueAcknowledged && age
      ? age >= AGE_CHECK_THRESHOLD && !selectedInsuranceContractId && !isOHCSide
      : false
  }, [
    selectedUser?.ssn,
    selectedNode?.features,
    freedomOfChoiceContinueAcknowledged,
    selectedInsuranceContractId,
    isOHCSide,
  ])

  if (shouldDisplayFreedomOfChoiceAllowedModal) {
    // Freedom of choice allowed modal (main flow)
    return (
      <GenericModalWrapper
        onContinue={() => {
          setFreedomOfChoiceContinueAcknowledged(true)
          setShouldDisplayPaymentTypeSelect(false)
        }}
        onClose={onBackToStart}
        modalType="freedom_of_choice_enabled"
      />
    )
  }

  if (shouldDisplayFreedomOfChoiceDisallowedModal) {
    // Freedom of choice disallowed modal (main flow)
    return (
      <GenericModalWrapper
        onContinue={() => {}}
        onClose={onBackToStart}
        modalType="freedom_of_choice_disabled"
      />
    )
  }

  if (shouldDisplayFreedomOfChoiceRedirectModal) {
    // Freedom of choice redirect modal (redirect flow)
    return (
      <GenericModalWrapper
        onContinue={() => {
          setFreedomOfChoiceContinueAcknowledged(true)
          setShouldDisplayPaymentTypeSelect(true)
        }}
        onClose={onBackToStart}
        modalType="freedom_of_choice_redirect"
      />
    )
  }

  return null
}
